import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useIsWebView, useToken, useWebViewLoading } from '../../../shared/hooks';
import { useMaster, MASTER_KEY } from '../../../shared/hooks/useMaster';
import { PdfButton } from '../../../shared/components/PdfButton';

import type { Inquiry, DateRange } from '../types';
import { useInquiryDetail } from '../hooks/useInquiryDetail';
import { SymptomLocationRows } from './SymptomLocationRows';
import { useInquiryFiles } from '../hooks/useInquiryFiles';
import type { FileType } from '../data/inquiryFilesRepository';
import { usePaymentInfo } from '../../payment/hooks/usePaymentInfo';
import { shouldShowBillingInfo } from '../../payment/hooks/billingAddressUtil';
import { BillingInfoBox } from '../../payment/components/BillingInfoBox';
import { formatBillingAddress } from '../../payment/utils/formatBillingAddress';
import { resolvePaymentNavigatePath } from '../../payment/utils/paymentNavigate';
import { formatDateTimeMinute } from '../../../shared/utils/datetime';

import './InquiryDetail.css';

// --- Helper Components ---

// 非カルテ進行（home-doctor / None）のときにフッターへ表示する案内メッセージ
// 該当のお問い合わせは Doctor 側で手続きされるため、homekarte WebView 上では
// 注文・納品・支払い手続きのアクションを実行できない旨をユーザーに伝える。
const NonKarteMessage: React.FC = () => (
  <p className="non-karte-message">homekarteから手続きが出来ません</p>
);

// 情報表示行コンポーネント（横並びレイアウト）
const InfoRow: React.FC<{ label: string, value: string | undefined, note?: string }> = ({ label, value, note }) => (
  <div className="info-row">
    <p className="info-label">{label}</p>
    <div className="info-value-wrap">
      <p className="info-value">{value || 'ー'}</p>
      {note && <p className="info-note">{note}</p>}
    </div>
  </div>
);

// 日付文字列から日付部分のみ取得（時間を除去）
const formatDateOnly = (dateStr: string | undefined): string | undefined => {
  if (!dateStr) return undefined;
  // "2025-01-15 10:00" or "2025/01/15 10:00" → "2025/01/15"
  return dateStr.split(' ')[0].replace(/-/g, '/');
};

// 日時文字列を整形（"2026-02-19 00:00:00" → { date: "2026/02/19", time: "00:00" }）
const parseDateTime = (dateTimeStr: string): { date: string; time: string } => {
  const [datePart, timePart] = dateTimeStr.split(' ');
  const date = datePart.replace(/-/g, '/');
  const time = timePart ? timePart.substring(0, 5) : '';
  return { date, time };
};

// DateRangeを表示用文字列に変換（同日なら日付を1回だけ表示）
const formatDateRange = (range: DateRange | undefined): string | undefined => {
  if (!range) return undefined;
  const { start, end } = range;
  if (!start && !end) return undefined;
  if (start && end) {
    const s = parseDateTime(start);
    const e = parseDateTime(end);
    if (s.date === e.date) {
      return `${s.date}\n${s.time} 〜 ${e.time}`;
    }
    return `${s.date} ${s.time}\n〜 ${e.date} ${e.time}`;
  }
  if (start) {
    const s = parseDateTime(start);
    return `${s.date} ${s.time} 〜`;
  }
  const e = parseDateTime(end!);
  return `〜 ${e.date} ${e.time}`;
};

/**
 * ステータスに応じたファイル種別を取得
 * API側のINQUIRY_FILE_TYPEに準拠
 */
const getFileTypeForStatus = (status: Inquiry['status']): FileType | null => {
  switch (status) {
    case '調査見積もり済':
    case '見積もりサイン済':
      return 'estimate';
    case '作業報告書確認':
      return 'reportAfter';
    case '作業完了':
      return 'mailSend';
    default:
      return null;
  }
};

/**
 * ファイル名から拡張子を除いた表示名を取得
 */
const getDisplayName = (fileName: string): string => {
  return fileName.replace(/\.pdf$/i, '');
};

// --- Main Component ---

function InquiryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const token = useToken();

  const inquiryId = id || '';

  const { data: inquiry, isLoading, error } = useInquiryDetail(inquiryId, token);

  // ファイル種別を決定（inquiryがない場合はnull）
  const fileType = inquiry ? getFileTypeForStatus(inquiry.status) : null;

  // ファイル一覧を取得（inquiryが読み込まれ、かつファイル表示が必要なステータスの場合のみ）
  // 注意: inquiryが読み込まれる前にfileType || 'estimate'でフォールバックすると、
  // 一瞬違うファイル件数が表示されてしまうため、inquiryが確定するまで取得しない
  const { files, isLoading: isFilesLoading } = useInquiryFiles(
    fileType ? inquiryId : '',  // fileTypeがnullの場合は空文字でスキップ
    fileType || 'estimate',
    token
  );

  // 支払い情報を取得
  // - 「支払い待ち」: 既存の支払方法遷移判定に使用
  // - 「作業完了」: 請求書払い時の請求先情報表示に使用（Phase 4）
  const needsPaymentInfo =
    inquiry?.status === '支払い待ち' || inquiry?.status === '作業完了';
  const { paymentInfo, estimateInfo, isLoading: isPaymentLoading } = usePaymentInfo(
    needsPaymentInfo ? inquiryId : '',
    token
  );

  const { getItems, isLoading: isMasterLoading } = useMaster();

  // 請求先情報Boxの表示判定（作業完了 + bill + billing情報あり。顧客区分は問わない）
  const showBillingInfo = shouldShowBillingInfo({
    status: inquiry?.status,
    paymentMethodCode: paymentInfo?.paymentMethodCode,
    billing: paymentInfo?.billing,
  });

  // 請求先住所表示用の都道府県名を解決
  const prefectureName = paymentInfo?.billing?.addressPrefectureId
    ? getItems(MASTER_KEY.PREFECTURES).find(
        p => p.itemId === paymentInfo.billing!.addressPrefectureId,
      )?.itemName ?? ''
    : '';

  const storageKey = `viewedDocs_${id}`;

  const [viewedDocs, setViewedDocs] = useState<Record<string, boolean>>(() => {
    const savedState = sessionStorage.getItem(storageKey);
    return savedState ? JSON.parse(savedState) : {};
  });

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(viewedDocs));
  }, [viewedDocs, storageKey]);

  // Flutter WebViewのローディング制御（データ取得完了時 & ページ再表示時）
  // コールバックrefを使用してDOMが実際に描画された後にhideLoadingを呼ぶ
  //
  // ローディング判定ロジック:
  // 1. inquiry取得中 → ローディング
  // 2. fileTypeがnull以外（ファイル表示が必要なステータス）の場合:
  //    - isFilesLoading中 → ローディング
  // 3. 支払い待ちステータスの場合:
  //    - isPaymentLoading中 → ローディング
  //
  // Note: useInquiryFilesフック側でinquiryIdが変わった瞬間に
  // isLoadingをtrueにするため、タイミングのズレは発生しない
  const needsFiles = fileType !== null;
  // 請求先情報Boxを表示する場合は都道府県名解決のためマスタが必要
  const needsMaster = showBillingInfo;
  const combinedLoading =
    isLoading ||
    (needsFiles && isFilesLoading) ||
    (needsPaymentInfo && isPaymentLoading) ||
    (needsMaster && isMasterLoading);
  const contentRef = useWebViewLoading(combinedLoading);

  // ローディング中はFlutter側のオーバーレイに任せるため、空のコンテナを返す
  if (combinedLoading) {
    return <div className="detail-container" />;
  }

  if (error || !inquiry) {
    return <div>お問い合わせが見つかりません。</div>;
  }

  // PDFをクリックしたときのハンドラ（ファイル名をキーとして使用）
  const handleViewDoc = (fileName: string) => {
    setViewedDocs(prev => ({ ...prev, [fileName]: true }));
  };

  // --- Main Content Definitions ---

  // Box 1: 依頼情報
  const getInquiryContentBox = (displayStatus: string) => (
    <div className="info-box">
      <h3 className="info-box-title">依頼情報</h3>
      <InfoRow label="受付状況" value={displayStatus} />
      <InfoRow label="受付日時" value={formatDateOnly(inquiry.requestDate)} />
      <SymptomLocationRows symptomLocations={inquiry.symptomLocations} />
      <InfoRow label="連絡依頼日時" value={formatDateRange(inquiry.contactDesiredDate)} />
      <InfoRow label="訪問希望日時" value={formatDateRange(inquiry.visitDesiredDate)} />
    </div>
  );

  // Box 2: 受付情報（データがある場合のみ表示）
  // PDF 指示により「受付担当者」「訪問連絡担当者」を「訪問担当者」に統合し
  // 1 行で表示する（manager 優先、未設定時は receptionManager をフォールバック）。
  const getReceptionContentBox = (_workerLabel: string) => {
    const hasData = inquiry.contractor || inquiry.receptionManager || inquiry.manager ||
      (inquiry.visitDate && inquiry.visitDate !== '日程調整中') || inquiry.plan || inquiry.amount;
    if (!hasData) return null;

    const worker = inquiry.manager || inquiry.receptionManager;
    return (
      <div className="info-box">
        <h3 className="info-box-title">受付情報</h3>
        <InfoRow label="受付事業者" value={inquiry.contractor} />
        <InfoRow label="訪問担当者" value={worker} />
        <InfoRow label="訪問日時" value={formatDateTimeMinute(inquiry.visitDate)} />
        <InfoRow label="訪問プラン" value={inquiry.plan} />
        <InfoRow
          label="料金"
          value={inquiry.amount}
          note="正式なお見積りは、訪問調査後にご案内いたします"
        />
      </div>
    );
  };

  // 2-boxレイアウトコンテンツ生成
  const getTwoBoxContent = (displayStatus: string, workerLabel: string) => (
    <>
      {getInquiryContentBox(displayStatus)}
      {getReceptionContentBox(workerLabel)}
    </>
  );

  // --- Footer Button Logic ---

  // カルテ進行かどうか（ドクター側で「カルテで進む」が選択された場合のみtrue）
  const isKarteProcessing = inquiry.processedSystem === 'home-karte';

  // 支払い画面への遷移ハンドラ（paymentInfoに基づいて遷移先を決定）
  //
  // 新仕様: 注文サイン前の支払方法選択（Phase 1）で支払方法が記録されている前提。
  // 遷移先決定ロジックは paymentNavigate.ts に切り出し済み（Doctor 側 Payment.tsx と整合）:
  // - cash / bill: CashPaymentConfirm（請求書払いも cash と同画面を流用）
  // - credit:      クレジットカード情報入力画面
  // - postpaid:    NP後払い確認画面
  // - 未記録（estimateInfo あり）: フォールバックとして支払方法選択画面（mode=change）
  const handlePaymentNavigate = () => {
    if (!id) return;
    const path = resolvePaymentNavigatePath(
      id,
      paymentInfo?.paymentMethodCode,
      paymentInfo?.isPaid ?? false,
      !!estimateInfo,
    );
    if (path) navigate(path);
  };

  const getActionButtons = (inquiry: Inquiry) => {
    switch (inquiry.status) {
      case '調査見積もり済':
        if (!isKarteProcessing) return [<NonKarteMessage key="non-karte" />];
        return [(
          <button key="1" className="button-primary" onClick={() => navigate(`/inquiries/${id}/documents`)}>
            見積・注文書の確認へ
          </button>
        )];

      case '作業報告書確認':
        if (!isKarteProcessing) return [<NonKarteMessage key="non-karte" />];
        return [(
          <button key="1" className="button-primary" onClick={() => navigate(`/inquiries/${id}/documents`)}>
            作業報告・納品の確認へ
          </button>
        )];

      case '支払い待ち':
        if (!isKarteProcessing) return [<NonKarteMessage key="non-karte" />];
        return [(
          <button key="1" className="button-primary" onClick={handlePaymentNavigate}>
            お支払い手続きへ
          </button>
        )];

      // ボタンが不要なステータス
      case '受付済':
      case '訪問確定済':
      case '調査中':
      case '見積もりサイン済':
      case '作業完了':
      default:
        return [];
    }
  };

  // --- Main Content Logic ---

  // タイトル（FEAT-3.2: 全ステータス統一）
  const getPageTitle = () => '修繕依頼の詳細';

  const mainContent = (() => {
    switch(inquiry.status) {
      case '受付中':
        return getTwoBoxContent('受付中', '訪問連絡担当者');

      case '受付済':
        return getTwoBoxContent('受付中', '訪問連絡担当者');

      case '訪問確定済':
        return getTwoBoxContent('訪問日確定', '訪問連絡担当者');

      case '調査中':
        return getTwoBoxContent('訪問作業中', '訪問担当者');

      case '調査見積もり済':
        return getTwoBoxContent('注文手続き', '訪問担当者');

      case '見積もりサイン済':
        return (
          <>
            {getTwoBoxContent('注文済み', '訪問担当者')}
            {files.map(file =>
              <PdfButton
                key={file.fileId}
                name={getDisplayName(file.fileName)}
                url={file.url}
                viewed={true}
                onView={() => handleViewDoc(file.fileName)}
              />
            )}
          </>
        );

      case '作業報告書確認':
        return getTwoBoxContent('納品手続き', '訪問担当者');

      case '作業完了':
        return (
          <>
            {getTwoBoxContent('作業完了', '訪問担当者')}
            {files.map(file =>
              <PdfButton
                key={file.fileId}
                name={getDisplayName(file.fileName)}
                url={file.url}
                viewed={true}
                onView={() => handleViewDoc(file.fileName)}
              />
            )}
            {showBillingInfo && paymentInfo?.billing && (
              <BillingInfoBox
                name={paymentInfo.billing.name}
                address={formatBillingAddress({
                  zipCode: paymentInfo.billing.zipCode,
                  prefectureName,
                  addressCity: paymentInfo.billing.addressCity,
                  addressDetail: paymentInfo.billing.addressDetail,
                  addressBuilding: paymentInfo.billing.addressBuilding,
                })}
              />
            )}
          </>
        );

      case 'キャンセル':
        return getTwoBoxContent('キャンセル', '訪問連絡担当者');

      case '支払い待ち':
        return getTwoBoxContent('支払手続き', '訪問担当者');

      default:
        return <p>情報を取得できませんでした。</p>;
    }
  })();

  // mainContentがnull = 想定外の状態（安全ガード）
  if (!mainContent) {
    return <div className="detail-container" />;
  }

  const buttons = getActionButtons(inquiry);

  return (
    <div ref={contentRef} className="detail-container">
      <main className="detail-content">
        <h2 className="content-title">{getPageTitle()}</h2>
        {mainContent}
      </main>
      {/* 既存のフッター構造を完全に維持 */}
      <footer className="detail-footer">
        {buttons}
        {!isWebView && (
          <button className="button-secondary" onClick={() => navigate(-1)}>戻る</button>
        )}
      </footer>
    </div>
  );
}

export default InquiryDetail;
